import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/app/providers/useAuth";
import {
  achievementsService,
  type Milestone,
  type Protest,
} from "@/features/settings/services/achievements.service";

type AchievementState = {
  protests: Protest[];
  milestones: Milestone[];
  statistics: {
    totalProtests: number;
    completedProtests: number;
    wonProtests: number;
    lostProtests: number;
    totalSavings: number;
    averageReduction: number;
  };
  active_protest: number;
  completed: number;
  won: number;
  lost: number;
  total_achievement: number;
};

export const useAchievements = (userId?: string) => {
  const { user } = useAuth();
  const effectiveUserId = userId || (user?.id ? String(user.id) : undefined);

  const name =
    user?.first_name || user?.last_name ? `${user?.first_name} ${user?.last_name}` : "User";
  const [achievements, setAchievements] = useState<AchievementState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAchievements = useCallback(async () => {
    if (!effectiveUserId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await achievementsService.getAchievements();
      const isSuccess = response.status === "success" || !!response.success;

      if (isSuccess && response.data) {
        setAchievements({
          ...response.data,
          active_protest: response.data.active_protest,
          completed: response.data.completed,
          won: response.data.won,
          lost: response.data.lost,
          total_achievement: response.data.total_achievement,
        });
      } else {
        setError(response.message || "Failed to load achievements");
      }
    } catch (err) {
      console.error("Error fetching achievements:", err);
      setError("Unable to load achievements. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [effectiveUserId]);

  useEffect(() => {
    void fetchAchievements();
  }, [fetchAchievements]);

  const totalProtests = achievements?.active_protest ?? 0;
  const completedProtests = achievements?.completed ?? 0;
  const wonProtests = achievements?.won ?? 0;
  const lostProtests = achievements?.lost ?? 0;
  const totalSavings = achievements?.statistics?.totalSavings ?? 0;
  const averageReduction = achievements?.statistics?.averageReduction ?? 0;

  const hasCompletedProtests = completedProtests > 0;
  const hasSavings = (achievements?.total_achievement ?? 0) > 0;

  const formattedSavings = totalSavings.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const formattedPercentage = `${averageReduction.toFixed(1)}%`;

  const winRate = completedProtests > 0 ? (wonProtests / completedProtests) * 100 : 0;
  const winRateDisplay = `Win Rate: ${winRate.toFixed(1)}% (${wonProtests}/${completedProtests})`;

  const statusSummary =
    totalProtests === 0
      ? "No protests filed"
      : completedProtests === 0
        ? "No completed protests"
        : `${completedProtests} completed`;

  const baseMilestones = achievements?.milestones?.filter((m) => m.achieved) ?? [];

  const milestones: Milestone[] = [...baseMilestones];
  if (achievements && achievements.active_protest >= 5) {
    const activeProtesterExists = milestones.some((m) => m.id === "active_protester");
    if (!activeProtesterExists) {
      milestones.push({
        id: "active_protester",
        title: "Active Protester",
        description: "Filed 5+ property tax protests",
        icon: "trending-up",
        color: "#FF5722",
        achieved: true,
      });
    }
  }

  return {
    achievements,
    milestones,
    loading,
    error,
    name,
    formattedSavings,
    formattedPercentage,
    winRateDisplay,
    statusSummary,
    hasCompletedProtests,
    hasSavings,
    refreshAchievements: fetchAchievements,
    totalProtests,
    completedProtests,
    wonProtests,
    lostProtests,
    totalSavings,
    averageReduction,
  };
};
